// React
import { useMemo, useState } from "react";

// MUI
import { Box, Fade, Stack, Typography } from "@mui/material";

// Hooks
import { useCitContextData } from "@DataPages/context/cit/CitContext";
import { useAllDealsContextData } from "@DataPages/context/cit/AllDealsContext";

// Utils
import { and } from "@Common/helpers/sonar";
import { generateAnalyticsData } from "@DataPages/helpers/financeManagement/cit/analytics";
import { numberToReadableFormat } from "@Common/helpers/number";
import { formatNumberCompact } from "@Common/helpers/number-new";

// Components
import { LoadingSpinner } from "@Layouts/components/LoadingSpinner";
import { AnimatedNumber } from "@DataPages/components/FniManagement/components/CIT/AnimatedNumber";
import MultiGroupedBarChartAnalyticsSkeleton from "@DataPages/layouts/financeManagement/skeletons/MultiGroupedBarChartAnalyticsSkeleton.tsx";
import MultiBarChartGraphLegend from "@DataPages/layouts/financeManagement/widgets/MultiBarChartGraphLegend";
import GroupedBarChart from "@Layouts/components/Charts/GroupedBarChart/GroupedBarChart";

// Types
import { IDeal } from "@Common/types/MyCit/model";
import { ISystemSettings } from "@Settings/types/Common/model";
import { IPeriodTreeSelection } from "@Layouts/components/SideBars/PayPeriodSideBar/interfaces";
import { IAnalyticsData } from "@DataPages/components/FniManagement/components/CIT/GradientBarGraph/GradientBarGraph";

type TAllDealsAnalyticsProps = Readonly<{
	selectedPayPeriod: IPeriodTreeSelection;
	isLoading: boolean;
	isFetching: boolean;
	data: IDeal[];
}>;

const colorByStatus = {
	delivered: "#FFB507",
	closed: "#2357BE",
	finalized: "#009245",
};

type TDealStatus = "delivered" | "closed" | "finalized";

function getGraphWidth(citSettings: ISystemSettings) {
	return parseInt((citSettings?.citGraphWidth ?? "").replace(" Days", ""), 10) || 0;
}

export default function AllDealsAnalyticsSection(props: TAllDealsAnalyticsProps) {
	const { selectedPayPeriod, data: deals, isLoading, isFetching } = props ?? {};

	const { citSettings } = useCitContextData();
	const { filteredAllDeals } = useAllDealsContextData();

	const [hoveredAge, setHoveredAge] = useState("");

	const citGraphWidth = getGraphWidth((citSettings ?? {}) as ISystemSettings);

	const maxValues = citSettings?.citMaxGraphValues ?? {
		maxUnfundedDollars: 5,
		maxUnfundedContracts: 200,
	};

	const unfundedDeals = useMemo(() => {
		if (!filteredAllDeals?.length) {
			return [];
		}

		return deals?.filter((deal) => filteredAllDeals.includes(deal._id) && !deal.funded);
	}, [filteredAllDeals]);

	const { totalUnfundedDollars, totalUnfundedContracts } = useMemo(() => {
		const unfundedDollars = generateAnalyticsData({
			deals: unfundedDeals,
			graphWidth: citGraphWidth,
			propToUse: "balance",
		});

		const unfundedContracts = generateAnalyticsData({
			deals: unfundedDeals,
			graphWidth: citGraphWidth,
			propToUse: "dealStatus",
		});

		return { totalUnfundedDollars: unfundedDollars, totalUnfundedContracts: unfundedContracts };
	}, [unfundedDeals, citGraphWidth]);

	const hasMoreData = and(!isLoading, isFetching);

	if (isLoading) {
		return <MultiGroupedBarChartAnalyticsSkeleton showSeverityBar={false} />;
	}

	const accumulateAnalyticValues = (data: IAnalyticsData[], prop: "quantity" | "amount") => {
		const property = prop === "amount" ? "exactAmount" : prop;
		return data.reduce((acc, cur) => acc + Number(cur?.[property]), 0);
	};

	/********************************************* HANDLERS ************************************************/
	const handleHoverBar = (age: string) => {
		setHoveredAge(age);
	};

	const handleHoverOutBar = () => {
		setHoveredAge("");
	};

	return (
		<Stack
			component={"section"}
			id={"f&i-all-deals-analytics"}
			spacing={3}
			justifyContent={"space-between"}
			alignItems={"center"}
			pt={4}
			pb={6.5}
			position={"relative"}
		>
			<Typography variant={"h3"} className={"page-title"} alignSelf={"self-start"} id={"f&i-all-deals-analytics-title"}>
				{selectedPayPeriod?.titleExtended} Deals Analytics
			</Typography>
			<AllDealsPaginatedSpinner hasMoreData={hasMoreData} />
			{/* Graphs */}
			<Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"} width={"100%"}>
				<Stack spacing={7}>
					<Typography className={"!text-default !font-montserrat !text-2xl !font-medium"} component="h3">
						{"Unfunded Dollars: "} <sup>$</sup>
						<AnimatedNumber finalValue={accumulateAnalyticValues(totalUnfundedDollars ?? [], "amount")} />
					</Typography>
					<GroupedBarChart
						syncId="all-deals-analytics-grouped"
						data={totalUnfundedDollars}
						bars={[
							{ dataKey: "delivered", color: "#FFB507", label: "Delivered" },
							{ dataKey: "closed", color: "#2357BE", label: "Closed" },
							{ dataKey: "finalized", color: "#009245", label: "Finalized" },
						]}
						tooltip={{
							useCustomizedTooltip: true,
							showTotal: true,
							valueFormatter: (value, dataKey) => {
								return (
									<Stack direction={"row"} justifyContent={"space-between"}>
										<Typography
											className={`!text-${colorByStatus[dataKey as TDealStatus]} !font-sans !text-xs !font-bold`}
										>
											$
										</Typography>
										<Typography
											className={`!text-${colorByStatus[dataKey as TDealStatus]} !font-sans !text-xs !font-bold`}
										>
											{formatNumberCompact(value)}
										</Typography>
									</Stack>
								);
							},
						}}
						totalKey="total"
						parentBarColor="#ECF0F6"
						xAxis={{ dataKey: "age", label: "Transit (days)" }}
						yAxis={{
							label: "Amount ($)",
							maxValue: maxValues.maxUnfundedDollars,
							tickFormatter: (value) => `$ ${formatNumberCompact(value)}`,
						}}
						hoveredValue={hoveredAge}
						onBarHover={handleHoverBar}
						onBarLeave={handleHoverOutBar}
					/>
				</Stack>
				<Stack spacing={7}>
					<Typography className={"!text-default !font-montserrat !text-2xl !font-medium"} component="h3">
						{"Unfunded Contracts: "}
						<AnimatedNumber finalValue={accumulateAnalyticValues(totalUnfundedContracts ?? [], "quantity")} />
					</Typography>

					<GroupedBarChart
						syncId="all-deals-analytics-grouped"
						data={totalUnfundedContracts}
						bars={[
							{ dataKey: "delivered", color: "#FFB507", label: "Delivered" },
							{ dataKey: "closed", color: "#2357BE", label: "Closed" },
							{ dataKey: "finalized", color: "#009245", label: "Finalized" },
						]}
						tooltip={{
							useCustomizedTooltip: true,
							showTotal: true,
							valueFormatter: numberToReadableFormat,
						}}
						totalKey="total"
						parentBarColor="#ECF0F6"
						xAxis={{ dataKey: "age", label: "Transit (days)" }}
						yAxis={{
							label: "# of Contracts",
							maxValue: maxValues.maxUnfundedContracts,
						}}
						hoveredValue={hoveredAge}
						// Callbacks
						onBarHover={handleHoverBar}
						onBarLeave={handleHoverOutBar}
					/>
				</Stack>
			</Stack>
			<MultiBarChartGraphLegend />
		</Stack>
	);
}

type AllDealsPaginatedSpinnerProps = {
	hasMoreData: boolean;
};
function AllDealsPaginatedSpinner(props: AllDealsPaginatedSpinnerProps) {
	const { hasMoreData } = props;

	if (!hasMoreData) {
		return null;
	}

	return (
		<Box component={"span"} alignSelf={"self-start"} position={"absolute"} top={45}>
			<Fade in={hasMoreData} timeout={400}>
				<Box component={"span"}>
					<LoadingSpinner label="All Deals are still loading..." />
				</Box>
			</Fade>
		</Box>
	);
}
